import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@roger/ui-components';
import { Patient } from '../store/slices/patientsSlice';
import { API_BASE_URL } from '../services/api';

interface PatientSnapshotDialogProps {
  patient: Patient | null;
  open: boolean;
  onClose: () => void;
}

interface EligibilityCheckResult {
  status: string;
  response_date: string;
  [key: string]: unknown;
}

interface PatientWithAuth extends Patient {
  authorization_status?: string;
}

const PatientSnapshotDialog: React.FC<PatientSnapshotDialogProps> = ({
  patient,
  open,
  onClose,
}) => {
  const [eligibility, setEligibility] = useState<EligibilityCheckResult | null>(
    null
  );
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [priorAuthStatus, setPriorAuthStatus] = useState<string>('N/A');

  // Fetch eligibility check result
  useEffect(() => {
    if (open && patient?.id) {
      setEligibilityLoading(true);
      setEligibilityError(null);
      fetch(`${API_BASE_URL}/eligibility?patient_id=${patient.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })
        .then(res => res.json())
        .then(checks => {
          if (Array.isArray(checks) && checks.length > 0) {
            // Sort by response_date descending, pick latest
            const sorted = [...checks].sort(
              (a, b) =>
                new Date(b.response_date).getTime() -
                new Date(a.response_date).getTime()
            );
            setEligibility(sorted[0]);
          } else {
            setEligibility(null);
          }
        })
        .catch(() => {
          setEligibilityError('Could not fetch eligibility check result');
          setEligibility(null);
        })
        .finally(() => setEligibilityLoading(false));
    }
  }, [open, patient?.id]);

  // Fetch prior auth status if available from patient object (if present)
  useEffect(() => {
    if (open && patient) {
      // If patient has a prior auth status field, use it; otherwise, N/A
      // This is a placeholder; update as needed if you have a real field
      const patientWithAuth = patient as PatientWithAuth;
      if (patientWithAuth.authorization_status) {
        setPriorAuthStatus(patientWithAuth.authorization_status);
      } else {
        setPriorAuthStatus('N/A');
      }
    }
  }, [open, patient]);

  // Calculate age from date_of_birth
  const getAge = (dob: string) => {
    if (!dob) return '--';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (!patient) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='max-w-md'>
        <div className='flex items-center justify-between mb-2'>
          <div className='text-lg font-semibold'>{patient.name}</div>
        </div>
        <div className='space-y-1 text-sm'>
          <div>
            <span className='font-medium text-muted-foreground'>MRN:</span>{' '}
            {patient.mrn || 'N/A'}{' '}
            <span className='ml-4 font-medium text-muted-foreground'>Age:</span>{' '}
            {getAge(patient.date_of_birth)}
          </div>
          <div>
            <span className='font-medium text-muted-foreground'>Phone:</span>{' '}
            {patient.phone || 'N/A'}
          </div>
          <div>
            <span className='font-medium text-muted-foreground'>Address:</span>{' '}
            {patient.address || 'N/A'}
          </div>
          <div className='pl-[54px]'>
            {[patient.city, patient.state].filter(Boolean).join(', ') || 'N/A'}
          </div>
          {/* Eligibility Check Result */}
          <div>
            <span className='font-medium text-muted-foreground'>
              Eligibility:
            </span>{' '}
            {eligibilityLoading
              ? 'Loading...'
              : eligibility
                ? eligibility.status || 'Completed'
                : eligibilityError || 'N/A'}
            {eligibility && eligibility.response_date && (
              <span className='ml-2 text-xs text-muted-foreground'>
                (Last checked:{' '}
                {new Date(eligibility.response_date).toLocaleDateString()})
              </span>
            )}
          </div>
          {/* Prior Auth Status */}
          <div>
            <span className='font-medium text-muted-foreground'>
              Prior Auth Status:
            </span>{' '}
            {priorAuthStatus}
          </div>
          <div>
            <span className='font-medium text-muted-foreground'>
              Primary Physician:
            </span>{' '}
            --
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PatientSnapshotDialog;
